import React, { useState, useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import LoadingSpinner from "@/components/loading/LoadingSpinner";
import "./address-map.css";

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const AddressMap = ({ initialLocation, onLocationChange }) => {
  const [departureLocation, setDepartureLocation] = useState(
    initialLocation || ""
  );
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [coordinates, setCoordinates] = useState({ lat: null, lon: null });

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const locationCache = useRef({});
  const mapInitialized = useRef(false);

  // Khởi tạo bản đồ
  const initMap = useCallback(() => {
    if (mapRef.current) return;

    const vietnamBounds = [
      [8.18, 102.14], // Tây Nam
      [23.39, 109.47], // Đông Bắc
    ];

    const map = L.map("map", {
      maxBounds: vietnamBounds,
      maxBoundsViscosity: 1.0,
      minZoom: 5,
    }).setView([16.0471, 108.2062], 6);

    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    markerRef.current = L.marker([16.0471, 108.2062]).addTo(map);
  }, []);

  // Cập nhật bản đồ và marker
  const updateMap = (lat, lon, zoom = 13) => {
    if (mapRef.current && markerRef.current) {
      const map = mapRef.current;
      const marker = markerRef.current;
      const latNum = parseFloat(lat);
      const lonNum = parseFloat(lon);

      const vietnamBounds = [
        [8.18, 102.14],
        [23.39, 109.47],
      ];
      if (
        latNum >= vietnamBounds[0][0] &&
        latNum <= vietnamBounds[1][0] &&
        lonNum >= vietnamBounds[0][1] &&
        lonNum <= vietnamBounds[1][1]
      ) {
        map.setView([latNum, lonNum], zoom);
        marker.setLatLng([latNum, lonNum]);
        setCoordinates({ lat: latNum, lon: lonNum });
      }
    }
  };

  // Tìm kiếm địa điểm
  const fetchLocation = async (query, initialLoad = false) => {
    if (!query || query.trim() === "") {
      setSuggestions([]);
      setIsLoadingSuggestions(false);
      return;
    }

    if (locationCache.current[query]) {
      const cachedData = locationCache.current[query];
      if (!initialLoad) {
        setSuggestions(cachedData);
      }
      if (cachedData.length > 0) {
        const { lat, lon } = cachedData[0];
        updateMap(lat, lon);
      }
      return;
    }

    setIsLoadingSuggestions(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          query
        )}&format=json&limit=10&countrycodes=vn&addressdetails=1`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      locationCache.current[query] = data;

      if (!initialLoad) {
        setSuggestions(data); // Hiển thị gợi ý khi người dùng nhập
      }
      if (data.length > 0) {
        const { lat, lon } = data[0];
        updateMap(lat, lon, 16);
      } else {
        setSuggestions([]); // Không có kết quả thì xóa gợi ý
      }
    } catch (error) {
      console.error("Error fetching location:", error);
      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const debouncedFetchLocation = useCallback(debounce(fetchLocation, 500), []);

  // Xử lý initialLocation khi khởi tạo
  useEffect(() => {
    if (!mapInitialized.current) {
      initMap();
      mapInitialized.current = true;
    }

    if (initialLocation && initialLocation !== departureLocation) {
      setDepartureLocation(initialLocation);
      const coordMatch = initialLocation.match(/Lat: ([\d.]+), Lon: ([\d.]+)/);
      if (coordMatch) {
        const lat = coordMatch[1];
        const lon = coordMatch[2];
        updateMap(lat, lon, 16);
      } else if (initialLocation.trim()) {
        fetchLocation(initialLocation, true);
      } else {
        updateMap(16.0471, 108.2062, 6);
      }
    } else if (!initialLocation) {
      updateMap(16.0471, 108.2062, 6);
    }
  }, [initialLocation, initMap]);

  // Xử lý thay đổi input
  const handleDepartureLocationChange = (e) => {
    const value = e.target.value;
    setDepartureLocation(value);
    onLocationChange({ address: value, coordinates: null });
    if (value.length > 2) {
      // Giảm xuống 2 để kiểm tra nhanh hơn
      debouncedFetchLocation(value, false);
    } else {
      setSuggestions([]);
    }
  };

  // Chọn gợi ý
  const handleSelectSuggestion = (suggestion) => {
    const selectedLocation = suggestion.display_name;
    const coords = {
      lat: parseFloat(suggestion.lat),
      lon: parseFloat(suggestion.lon),
    };
    setDepartureLocation(selectedLocation);
    updateMap(suggestion.lat, suggestion.lon, 16);
    setSuggestions([]);
    onLocationChange({ address: selectedLocation, coordinates: coords });
  };

  return (
    <div className="map-component">
      <div className="mb-3 position-relative">
        <input
          type="text"
          className="form-control"
          placeholder="Nhập địa điểm của bạn ..."
          value={departureLocation}
          onChange={handleDepartureLocationChange}
        />
        {isLoadingSuggestions && (
          <LoadingSpinner
            type="circle"
            color="#3498db"
            size={20}
            text="Đang tìm kiếm..."
          />
        )}
        {suggestions.length > 0 && !isLoadingSuggestions && (
          <ul className="suggestions-list">
            {suggestions.map((suggestion, idx) => (
              <li key={idx} onClick={() => handleSelectSuggestion(suggestion)}>
                {suggestion.display_name}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="mb-3">
        <label className="form-label">Bản đồ địa chỉ</label>
        <div id="map" style={{ height: "200px", width: "70%" }}></div>
      </div>
    </div>
  );
};

export default AddressMap;
